import type {SelectedDay} from '../../types';

import {cn} from '@ds/helpers';
import moment from 'moment-jalaali';

import {JALALI_MONTH_NAMES, JALALI_WEEK_DAYS} from '../../utils/constants';
import {convertJalaliToGregorian, getJalaliFirstWeekday} from '../../utils/jalali';

export type MonthCalendarProps = {
  month: number;
  selectedDay: number;
  selectedDayKey?: string;
  setter: React.Dispatch<React.SetStateAction<SelectedDay>>;
  filterSetter: (value: SelectedDay) => void;
  currentDay: number;
  enableDaysCount?: number;
};

const dayColBase =
  'flex h-[45px] w-[45px] flex-col items-center justify-center rounded-lg transition-all duration-200';

function TodayDot() {
  return <div className="size-[5px] rounded-full bg-orange-main" />;
}

export function MonthCalendar({
  month,
  filterSetter,
  selectedDayKey,
  setter,
  currentDay,
  enableDaysCount
}: MonthCalendarProps) {
  const currentYear = moment().jYear();
  const firstDayOfMonth = moment(`${currentYear}-${month + 1}-1`, 'jYYYY-jM-jD');
  const firstDayOfWeek = getJalaliFirstWeekday(currentYear, month);
  const daysInMonth = firstDayOfMonth.daysInMonth();

  const previousMonth = month === 0 ? 11 : month - 1;
  const previousYear = month === 0 ? currentYear - 1 : currentYear;
  const lastDayOfPreviousMonth = moment(
    `${previousYear}-${previousMonth + 1}-1`,
    'jYYYY-jM-jD'
  ).daysInMonth();

  const daysFromPreviousMonth = firstDayOfWeek;
  const totalDaysInGrid = 42;
  const daysFromNextMonth = totalDaysInGrid - daysFromPreviousMonth - daysInMonth;

  const handleSelectedItem = (
    day: number,
    isCurrentMonth: boolean,
    isPreviousMonth: boolean,
    isNextMonth: boolean
  ) => {
    if (isPreviousMonth) return;

    if (isCurrentMonth) {
      const next: SelectedDay = {
        day,
        dayKey: `current-${day}`,
        date: convertJalaliToGregorian(`${currentYear}-${month + 1}-${day}`)
      };
      filterSetter(next);
      setter(next);
    } else if (isNextMonth) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? currentYear + 1 : currentYear;
      const next: SelectedDay = {
        day,
        dayKey: `next-${day}`,
        date: convertJalaliToGregorian(`${nextYear}-${nextMonth + 1}-${day}`)
      };
      filterSetter(next);
      setter(next);
    }
  };

  const renderDay = (
    dayNumber: number,
    isCurrentMonth: boolean,
    isPreviousMonth: boolean,
    isNextMonth: boolean
  ) => {
    const isToday = isCurrentMonth && currentDay === dayNumber;
    const currentDayKey = isCurrentMonth ? `current-${dayNumber}` : `next-${dayNumber}`;
    const isSelected = selectedDayKey === currentDayKey;
    const key = `${isCurrentMonth ? 'current' : isPreviousMonth ? 'prev' : 'next'}-${dayNumber}`;

    if (enableDaysCount === undefined) {
      const isDisabled = isCurrentMonth && currentDay >= dayNumber;
      return (
        <div
          key={key}
          className={cn(
            dayColBase,
            isSelected && 'bg-orange-main text-white',
            isDisabled && 'text-gray-06',
            isPreviousMonth && 'cursor-not-allowed text-gray-05 opacity-60'
          )}
          onClick={() => {
            if ((isCurrentMonth && dayNumber > currentDay) || isNextMonth) {
              handleSelectedItem(dayNumber, isCurrentMonth, isPreviousMonth, isNextMonth);
            }
          }}
        >
          {dayNumber}
          {isToday && <TodayDot />}
        </div>
      );
    }

    const remainingDaysInCurrentMonth = daysInMonth - currentDay;
    const isCurrentMonthDisabled =
      isCurrentMonth && (currentDay >= dayNumber || dayNumber > currentDay + enableDaysCount);
    const isNextMonthDisabled =
      isNextMonth && dayNumber > enableDaysCount - remainingDaysInCurrentMonth;
    const isDisabled = isCurrentMonthDisabled || isNextMonthDisabled;

    return (
      <div
        key={key}
        className={cn(
          dayColBase,
          isSelected && 'bg-orange-main text-white',
          isDisabled && 'text-gray-06',
          isPreviousMonth && 'cursor-not-allowed text-gray-05 opacity-60'
        )}
        onClick={() => {
          const remaining = daysInMonth - currentDay;
          const isCurrentMonthClickable =
            isCurrentMonth && dayNumber > currentDay && dayNumber <= currentDay + enableDaysCount;
          const isNextMonthClickable = isNextMonth && dayNumber <= enableDaysCount - remaining;

          if (isCurrentMonthClickable || isNextMonthClickable) {
            handleSelectedItem(dayNumber, isCurrentMonth, isPreviousMonth, isNextMonth);
          }
        }}
      >
        {dayNumber}
        {isToday && <TodayDot />}
      </div>
    );
  };

  const generateCalendarDays = () => {
    const days: React.ReactNode[] = [];

    for (let i = daysFromPreviousMonth - 1; i >= 0; i -= 1) {
      days.push(renderDay(lastDayOfPreviousMonth - i, false, true, false));
    }
    for (let i = 1; i <= daysInMonth; i += 1) {
      days.push(renderDay(i, true, false, false));
    }
    for (let i = 1; i <= daysFromNextMonth; i += 1) {
      days.push(renderDay(i, false, false, true));
    }

    return days;
  };

  return (
    <div className="flex min-h-[100px] w-full flex-col">
      <div className="flex w-full justify-items-center gap-2.5 border-b border-gray-04 px-4 py-2">
        {JALALI_WEEK_DAYS.map((label) => (
          <label key={label} className="flex-1 text-center text-gray-06">
            {label}
          </label>
        ))}
      </div>
      <div className="flex w-full bg-gray-02 px-4 py-2">
        <label className="text-[1.4em]">
          {JALALI_MONTH_NAMES[month]} {currentYear}
        </label>
      </div>
      <div className="grid w-full grid-cols-7 gap-[5px] bg-gray-02 px-4 py-2">
        {generateCalendarDays()}
      </div>
    </div>
  );
}

export default MonthCalendar;
